import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Check, Loader2 } from 'lucide-react';
import { signUp, signIn } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import './Signup.css';

const PERKS = [
  'Free forever plan — no credit card needed',
  'Access to 1,000+ AI-indexed tools',
  '50 AI-powered searches per month',
  'Save and organize your tool stacks',
];

export default function Signup() {
  const [mode, setMode] = useState('signup'); // signup | signin
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | welcome | error
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { user, displayName } = useAuth();

  // If already signed in, redirect home
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const validate = () => {
    const e = {};
    if (mode === 'signup' && !form.name.trim()) e.name = 'Name is required';
    if (!form.email.includes('@')) e.email = 'Enter a valid email';
    if (form.password.length < 8) e.password = 'Minimum 8 characters';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('loading');
    setErrorMsg('');
    try {
      if (mode === 'signup') {
        await signUp(form.email, form.password, form.name);
        setStatus('success');
      } else {
        await signIn(form.email, form.password);
        setStatus('welcome');
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (err) {
      setStatus('error');
      // Make Supabase errors human-readable
      const msg = err.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        setErrorMsg('An account with this email already exists. Sign in instead.');
      } else if (msg.includes('Invalid login credentials')) {
        setErrorMsg('Incorrect email or password. Please try again.');
      } else if (msg.includes('Email not confirmed')) {
        setErrorMsg('Please check your inbox and confirm your email first.');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    }
  };

  const update = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: undefined }));
    if (status === 'error') setStatus('idle');
  };

  const switchMode = (m) => {
    setMode(m);
    setErrors({});
    setStatus('idle');
    setErrorMsg('');
  };

  return (
    <main className="signup-page">
      {/* Left panel */}
      <div className="signup__left">
        <div className="signup__left-inner">
          <Link to="/" className="navbar__logo" style={{ marginBottom: 'var(--sp-12)' }}>
            <div className="navbar__logo-icon"><img src="/brain-64.png" alt="ToolMind" className="navbar__logo-img" /></div>
            <span className="navbar__logo-text">ToolMind</span>
            <span className="navbar__logo-badge">AI</span>
          </Link>
          <h2 className="signup__left-title">
            Find the <span className="grad-text">right tools</span><br />in minutes, not days.
          </h2>
          <p className="signup__left-sub">
            Join 50,000+ builders who use ToolMind to discover, compare, and manage their software stack.
          </p>
          <ul className="signup__perks">
            {PERKS.map(p => (
              <li key={p} className="signup__perk">
                <div className="signup__perk-check"><Check size={13} /></div>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <div className="signup__trust">
            <div className="signup__trust-avatars">
              {['A','M','S','J','K'].map((l,i) => (
                <div key={i} className="signup__trust-avatar" style={{ marginLeft: i ? -10 : 0 }}>{l}</div>
              ))}
            </div>
            <div>
              <div className="signup__trust-main">Loved by 50,000+ builders</div>
              <div className="signup__trust-sub">4.9 / 5 on Product Hunt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="signup__right">
        <div className="signup__form-wrap glass-hi">

          {/* Mode toggle */}
          <div className="signup__mode-toggle">
            <button
              className={`signup__mode-btn ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >Create account</button>
            <button
              className={`signup__mode-btn ${mode === 'signin' ? 'active' : ''}`}
              onClick={() => switchMode('signin')}
            >Sign in</button>
          </div>

          {/* Success state (sign up only) */}
          {/* Welcome flash after sign in */}
          {status === 'welcome' ? (
            <div className="signup__success">
              <div className="signup__success-icon"><Check size={28} /></div>
              <h3 className="signup__success-title">Welcome back, {form.name || displayName}!</h3>
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', fontSize: 14 }}>
                Taking you home…
              </p>
            </div>

          ) : status === 'success' && mode === 'signup' ? (
            <div className="signup__success">
              <div className="signup__success-icon"><Check size={28} /></div>
              <h3 className="signup__success-title">Check your email!</h3>
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', fontSize: 14, lineHeight: 1.6 }}>
                We sent a confirmation link to <strong>{form.email}</strong>. Click it to activate your account, then come back to sign in.
              </p>
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginTop: 'var(--sp-4)' }}
                onClick={() => switchMode('signin')}
              >
                Go to sign in
              </button>
            </div>
          ) : (
            <>
              <div className="signup__form-header">
                <h3 className="signup__form-title">
                  {mode === 'signup' ? 'Create your account' : 'Welcome back'}
                </h3>
                <p className="signup__form-sub">
                  {mode === 'signup' ? 'Free forever · No credit card required' : 'Sign in to your ToolMind account'}
                </p>
              </div>

              {/* Error banner */}
              {status === 'error' && errorMsg && (
                <div className="signup__error-banner">{errorMsg}</div>
              )}

              <form className="signup__form" onSubmit={handleSubmit} noValidate>
                {mode === 'signup' && (
                  <div className={`signup__field ${errors.name ? 'error' : ''}`}>
                    <label className="signup__label">Full name</label>
                    <div className="signup__input-wrap">
                      <User size={15} className="signup__input-icon" />
                      <input
                        className="signup__input"
                        type="text"
                        placeholder="Alex Johnson"
                        value={form.name}
                        onChange={e => update('name', e.target.value)}
                        disabled={status === 'loading'}
                      />
                    </div>
                    {errors.name && <p className="signup__error">{errors.name}</p>}
                  </div>
                )}

                <div className={`signup__field ${errors.email ? 'error' : ''}`}>
                  <label className="signup__label">Email</label>
                  <div className="signup__input-wrap">
                    <Mail size={15} className="signup__input-icon" />
                    <input
                      className="signup__input"
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={e => update('email', e.target.value)}
                      disabled={status === 'loading'}
                    />
                  </div>
                  {errors.email && <p className="signup__error">{errors.email}</p>}
                </div>

                <div className={`signup__field ${errors.password ? 'error' : ''}`}>
                  <label className="signup__label">Password</label>
                  <div className="signup__input-wrap">
                    <Lock size={15} className="signup__input-icon" />
                    <input
                      className="signup__input"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Minimum 8 characters"
                      value={form.password}
                      onChange={e => update('password', e.target.value)}
                      disabled={status === 'loading'}
                    />
                    <button type="button" className="signup__pw-toggle" onClick={() => setShowPw(v => !v)}>
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {errors.password && <p className="signup__error">{errors.password}</p>}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary signup__submit"
                  disabled={status === 'loading'}
                >
                  {status === 'loading'
                    ? <><Loader2 size={16} className="tools-page__spinner" /> {mode === 'signup' ? 'Creating account…' : 'Signing in…'}</>
                    : <>{mode === 'signup' ? 'Create free account' : 'Sign in'} <ArrowRight size={16} /></>
                  }
                </button>

                {mode === 'signup' && (
                  <p className="signup__terms">
                    By signing up, you agree to our{' '}
                    <Link to="#" style={{ color: 'var(--text-accent)' }}>Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="#" style={{ color: 'var(--text-accent)' }}>Privacy Policy</Link>.
                  </p>
                )}
              </form>

              <p className="signup__signin">
                {mode === 'signup'
                  ? <>Already have an account? <button className="signup__switch-link" onClick={() => switchMode('signin')}>Sign in</button></>
                  : <>Don't have an account? <button className="signup__switch-link" onClick={() => switchMode('signup')}>Create one free</button></>
                }
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
